import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion'
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePopper } from 'react-popper'

/* eslint-disable react/display-name */
import { useMediaQuery } from '@sparkai/hooks'
import { cn } from '@sparkai/utils'

import { Mention } from '.'
import { Avatar } from '../../Avatar'
import { PersonProps } from '../EditorText'

interface MentionListProps extends SuggestionProps {
  mentions: PersonProps[]
  mentionsHidden?: boolean
}

interface MentionListActions {
  onKeyDown: (props: SuggestionKeyDownProps) => void
}

export const MentionList = forwardRef<MentionListActions, MentionListProps>(
  ({ clientRect, command, decorationNode, mentions, mentionsHidden, query }, ref) => {
    const referenceEl = useMemo(
      () =>
        clientRect
          ? { getBoundingClientRect: clientRect, contextElement: decorationNode ?? undefined }
          : null,
      [clientRect, decorationNode]
    )

    const filteredMentions = useMemo(() => {
      if (query === '') return mentions
      return mentions.filter((person) => person.name.toLowerCase().includes(query.toLowerCase()))
    }, [mentions, query])

    const handleCommand = (person: PersonProps) => {
      command({ id: person.id, label: person.name })
    }

    const [hoverIndex, setHoverIndex] = useState(0)

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        const { key } = event

        if (key === 'ArrowUp') {
          setHoverIndex((prev) => {
            const beforeIndex = prev - 1
            return beforeIndex >= 0 ? beforeIndex : 0
          })
          return true
        }

        if (key === 'ArrowDown') {
          setHoverIndex((prev) => {
            const afterIndex = prev + 1
            const peopleCount = mentions.length - 1
            return afterIndex < peopleCount ? afterIndex : peopleCount
          })
          return true
        }

        if (key === 'Enter') {
          handleCommand(filteredMentions[hoverIndex])
          return true
        }

        return false
      },
    }))

    const { isMobile } = useMediaQuery()

    const [el, setEl] = useState<HTMLDivElement | null>(null)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { styles, attributes } = usePopper(referenceEl as any, el, {
      placement: isMobile ? 'top-start' : 'bottom-start',
      strategy: 'fixed',
    })

    return createPortal(
      <div
        ref={setEl}
        className={cn(
          'mentionsContainer z-50 max-h-[300px] w-[258px] min-w-[258px] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-xl'
        )}
        style={styles.popper}
        {...attributes.popper}
      >
        {filteredMentions.length === 0 && (
          <p className='flex px-3 py-2 text-sm font-normal text-gray-400'>
            {mentionsHidden ? "Mentions aren't available during the audit session." : 'No Match Found'}
          </p>
        )}
        {filteredMentions.map((person, index) => (
          <Mention.Item
            key={person.id}
            isActive={index === hoverIndex}
            onMouseEnter={() => setHoverIndex(index)}
            onClick={() => handleCommand(person)}
          >
            <Avatar
              fallback={{ value: person.name }}
              imageProps={{ src: person.avatarUrl }}
              width={24}
              height={24}
            />
            {person.name}
          </Mention.Item>
        ))}
      </div>,
      document.body
    )
  }
)
